import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useQuote } from '@/contexts/QuoteContext';
import { motion, AnimatePresence } from 'framer-motion';
import { NoImage } from '../../../public';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Loader from './Loader';

interface QuoteIconProps {
  isMobileMenuOpen?: boolean;
  isSearchFocused?: boolean;
}

const QuoteItemImage: React.FC<{ src?: string; name?: string }> = ({ src, name }) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="w-mob-50 sm:w-tab-50 lg:w-web-70 h-mob-50 sm:h-tab-50 lg:h-web-70 flex-shrink-0 bg-white border border-gray-200 rounded-mob-4 sm:rounded-tab-4 lg:rounded-web-4 overflow-hidden relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <Loader className="scale-75" />
        </div>
      )}
      <Image
        src={src || NoImage}
        alt={name || "Product Image"}
        width={70}
        height={70}
        className={`w-full h-full object-contain transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setIsLoading(false)}
        onError={() => setIsLoading(false)}
      />
    </div>
  );
};

const QuoteIcon: React.FC<QuoteIconProps> = ({ isMobileMenuOpen = false, isSearchFocused = false }) => {
  const { quoteCount, quoteItems, removeFromQuote } = useQuote();

  // Trigger animation when quote count changes
  useEffect(() => {
    if (quoteCount > 0) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [quoteCount]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const pathname = usePathname();

  // Check if we should render the component
  const shouldRender = !pathname?.startsWith('/blogs') &&
    !pathname?.startsWith('/quote')

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Check if scrolling
      if (Math.abs(currentScrollY - lastScrollY) > 5) {
        setIsVisible(false);
      }

      lastScrollY = currentScrollY;

      // Clear previous timeout
      clearTimeout(scrollTimeout);

      // Set timeout to show icon again after scrolling stops
      scrollTimeout = setTimeout(() => {
        setIsVisible(true);
      }, 300);
    };

    // Only add scroll listener on mobile devices
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll);
      }
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Don't render anything if we're on a blog page
  if (!shouldRender) {
    return null;
  }

  return (
    <div className="fixed top-0 right-0 z-[200] pt-mob-55 sm:pt-tab-60 lg:pt-[0.725vw] sm:pr-[0vw] lg:pr-[0vw] lg:mt-[3vw]">
      {/* Quote Icon - Desktop */}
      <motion.div
        className="hidden group lg:flex items-center gap-web-15 lg:pb-web-6 relative z-[145] cursor-pointer"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        animate={{
          opacity: isSearchFocused ? 0 : 1,
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut"
        }}
      >
        <div className="relative transition-all duration-200">
          {/* Blue square background with Q */}
          <div className=" flex flex-col w-web-55 h-web-88 bg-PrimaryLightBlue rounded-l-mob-4 sm:rounded-tab-4 lg:rounded-r-web-4 items-center justify-center lg:pb-[0.6vw]">
            <span className=" text-web-30 font-normal">Q</span>
            <div className={`bg-SecondaryYellow text-PrimaryDarkBlue text-mob-10 sm:text-tab-10 lg:text-web-13 rounded-full w-mob-24 sm:w-tab-24 lg:w-web-24 h-mob-24 sm:h-tab-24 lg:h-web-24 flex items-center justify-center font-bold transition-all duration-300 ease-in-out ${isAnimating ? 'animate-breath' : ''
              }`}>
              {quoteCount > 99 ? '99+' : quoteCount}
            </div>
          </div>

          {/* Quote Count Badge - Yellow circle - Always show */}

        </div>
      </motion.div>

      {/* Quote Icon - Mobile/Tablet */}
      <motion.div
        className="flex group lg:hidden items-center gap-mob-10 sm:gap-tab-10 relative z-[145] cursor-pointer"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        animate={{
          opacity: isVisible && !isMobileMenuOpen && !isSearchFocused ? 1 : 0,
          y: isVisible && !isMobileMenuOpen ? 0 : -10
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut"
        }}
      >
        <div className="relative transition-all duration-200">
          {/* Blue square background with Q */}
          <div className="flex flex-row w-mob-68 sm:w-tab-80 h-mob-35 sm:h-tab-40 bg-PrimaryLightBlue rounded-l-mob-4 sm:rounded-tab-4 lg:rounded-r-web-4 items-center justify-center gap-mob-8 sm:gap-tab-12 lg:pb-[0.6vw]">
            <span className="text-mob-28 sm:text-tab-32 font-normal">Q</span>
            <div className={`bg-SecondaryYellow text-PrimaryDarkBlue text-mob-10 sm:text-tab-12 lg:text-web-10 rounded-full w-mob-24 sm:w-tab-28 lg:w-web-24 h-mob-24 sm:h-tab-28 lg:h-web-24 flex items-center justify-center font-normal transition-all duration-300 ease-in-out ${isAnimating ? 'animate-breath' : ''
              }`}>
              {quoteCount > 99 ? '99+' : quoteCount}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quote Slide Popover - Same style as DetailsSlidePopover */}
      <AnimatePresence>
        {isDropdownOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed w-full h-full left-0 top-0 bg-black/70 z-[148]"
              onClick={() => setIsDropdownOpen(false)}
            />

            {/* Slide Popover */}
            <motion.div
              key="popover"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.4 }}
              className="fixed z-[150] top-0 right-0 h-full bg-white w-full sm:w-[65vw] lg:w-[26.04vw] flex flex-col pt-mob-20 sm:pt-tab-30 lg:pt-web-30"
            >
              {/* Close button */}
              <div
                className="px-mob-23 sm:px-tab-23 lg:px-web-23 flex justify-end"
                onClick={() => setIsDropdownOpen(false)}
              >
                <div className="relative w-mob-59 sm:w-tab-59 lg:w-web-59 aspect-[59/59] cursor-pointer">
                  <Image
                    src="/images/close-btn.svg"
                    alt="close"
                    fill
                    sizes="59px"
                    className="object-cover object-center"
                  />
                </div>
              </div>

              {/* Header with title */}
              <div className="px-mob-23 sm:px-tab-23 lg:px-web-23 mb-mob-20 sm:mb-tab-20 lg:mb-web-20 lg:mt-web-30">
                <h2 className="text-mob-28 sm:text-tab-28 lg:text-web-40 text-PrimaryLightBlue  leading-[120%]">
                  Quotation List
                </h2>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-mob-23 sm:px-tab-23 lg:px-web-23 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-button]:hidden pb-[1vw]">
                {quoteItems.length === 0 ? (
                  <div className="flex-1 flex items-center justify-start py-mob-40 sm:py-tab-40 lg:py-web-40">
                    <div className="text-left">
                      <p className="text-mob-18 sm:text-tab-18 lg:text-web-18 text-[#5A5A5A]">Your quotation list is currently empty.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-mob-15 sm:space-y-tab-15 lg:space-y-web-15">
                    {quoteItems.map((item) => (
                      <div key={item.id} className="flex items-center bg-[#F6F6F6] border border-[#DADADA] rounded-mob-8 sm:rounded-tab-8 lg:rounded-web-8 gap-mob-12 sm:gap-tab-12 lg:gap-web-12 p-mob-12 sm:p-tab-12 lg:p-web-12">
                        {/* Product Image */}
                        <QuoteItemImage src={item.images} name={item.name || ""} />

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <h4 className="text-mob-14 sm:text-tab-14 lg:text-web-20  text-[#323232] leading-[120%] line-clamp-2">
                            {item.name}
                          </h4>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => removeFromQuote(item.id ?? "")}
                          className="flex-shrink-0 mt-auto p-mob-6 sm:p-tab-6 lg:p-web-6 text-gray-400 hover:text-red-600 transition-colors"
                          title="Remove from quote"
                        >
                          <svg className="w-mob-20 sm:w-tab-20 lg:w-web-24 h-mob-20 sm:h-tab-20 lg:h-web-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer Actions - Single button like in image */}
              {quoteItems.length > 0 && (
                <div className="relative">
                  {/* Gradient overlay above button */}
                  <div className="absolute bottom-full left-0 right-0 h-mob-50 sm:h-tab-50 lg:h-web-50 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>

                  <div className="px-mob-23 sm:px-tab-23 lg:px-web-23 pb-mob-20 sm:pb-tab-20 lg:pb-web-20 lg:mb-[6vw]">
                    <Link
                      href="/quote"
                      onClick={() => setIsDropdownOpen(false)}
                      className="w-full bg-PrimaryLightBlue text-white py-mob-14 sm:py-tab-14 lg:py-web-14 px-mob-20 sm:px-tab-20 lg:px-web-20 rounded-mob-6 sm:rounded-tab-6 lg:rounded-web-6 text-mob-16 sm:text-tab-16 lg:text-web-16 font-medium hover:bg-blue-600 transition-colors uppercase tracking-wider"
                    >
                      GO TO QUOTE PAGE
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuoteIcon; 